import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import DataSources from './DataSources';
import SidePanelDialog from './SidePaneDialog';
import { HeaderDialogType, SideDialog } from '@gui/constants/Panels';
import { useEnvStore } from '../../../../store/EnvStore';
import { useResourceStore } from '../../../../store/ResourceStore';
import ObservationManager from '../State/ObservationManager';
import SettingsMenu from './SettingsMenu';
import { Dotpoints01, InfoCircle, Settings01, Sun, Announcement03, BookOpen01 } from '@untitledui-pro/icons/line';
import SolarEnergyResources from '../SolarEnergyResources/SolarEnergyResources';
import { Lightbulb02, Edit05 } from '@untitledui-pro/icons/line';
import Suggestions from '../Suggestions';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { Visualization } from '@rendering/Visualizer';
import ReportIssue from './ReportIssue';
import { useCapabilities } from '../../../../demo/capabilities';
import { useSessionStore } from '../../../../store/SessionStore';
import { getSentence } from '@gui/constants/sentences';

type SettingsProps = {
    viewer: Visualization;
};

const RightSettings: React.FC<SettingsProps> = ({ viewer }) => {
    const [observationOpen, setObservationOpen] = useState(false);
    const gridId = useResourceStore((s) => s.gridId);
    const projectId = useResourceStore((s) => s.projectId);
    const { backendUrl, env } = useEnvStore();
    const canUseApi = env !== 'local-offline';
    const hasObservationReports = useSessionStore((state) => state.hasObservationReports);
    const { canOpenProjectPage } = useCapabilities();
    const [activeDialog, setActiveDialog] = useState<HeaderDialogType | null>(null);
    const closeDialog = useCallback(() => {
        setActiveDialog(null);
    }, []);

    const rightActions: SideDialog[] = [
        {
            type: HeaderDialogType.OBSERVATIONS,
            Icon: <Edit05 className="icon-size-big" />,
            title: 'OBSERVATION',
            tooltip: 'Create Observation',
            content: (
                <ObservationManager showContent={activeDialog === HeaderDialogType.OBSERVATIONS} canUseApi={canUseApi} gridId={gridId} />
            ),
            btnID: 'observationsBtn',
        },
        {
            type: HeaderDialogType.SUGGESTIONS,
            Icon: <Lightbulb02 className="icon-size-big" />,
            title: 'SUGGESTIONS',
            tooltip: 'Show Suggestions',
            content: <Suggestions showContent={activeDialog === HeaderDialogType.SUGGESTIONS} />,
            btnID: 'suggestionsBtn',
        },
        {
            type: HeaderDialogType.DATA_SOURCES,
            Icon: <InfoCircle className="icon-size-big" />,
            title: 'DATA SOURCES',
            tooltip: 'Data Sources',
            content: <DataSources showContent={activeDialog === HeaderDialogType.DATA_SOURCES} />,
            btnID: 'dataSourcesBtn',
        },
        {
            type: HeaderDialogType.SOLAR_ENERGY_RESOURCES,
            Icon: <Sun className="icon-size-big" />,
            title: 'SOLAR ENERGY PARAMETERS',
            tooltip: 'Solar Energy Parameters',
            content: <SolarEnergyResources showContent={activeDialog === HeaderDialogType.SOLAR_ENERGY_RESOURCES} />,
            btnID: 'solarEnergyResBtn',
        },
        {
            type: HeaderDialogType.BACK,
            Icon: <Dotpoints01 className="icon-size-big" />,
            title: 'OPEN PROJECT PAGE',
            tooltip: 'Open Project Page',
            onClick: () => window.open(`${backendUrl}/projects/${projectId}`, '_blank', 'noreferrer'),
            btnID: 'backBtn',
        },
        {
            type: HeaderDialogType.SETTINGS,
            Icon: <Settings01 className="icon-size-big" />,
            title: 'SETTINGS',
            tooltip: 'Settings',
            content: <SettingsMenu viewer={viewer} showContent={activeDialog === HeaderDialogType.SETTINGS} />,
            btnID: 'settingsBtn',
        },
        {
            type: HeaderDialogType.USER_GUIDE,
            Icon: <BookOpen01 className="icon-size-big" />,
            title: 'USER GUIDE',
            tooltip: 'User Guide',
            onClick: () => window.open('https://urbanmetrix.com/guide', '_blank', 'noreferrer'),
            btnID: 'userGuideBtn',
        },
        {
            type: HeaderDialogType.REPORT_ISSUE,
            Icon: <Announcement03 className="icon-size-big" />,
            title: 'REPORT ISSUE',
            tooltip: 'Report issue',
            content: <ReportIssue showContent={activeDialog === HeaderDialogType.REPORT_ISSUE} onFeedbackSent={closeDialog} />,
            btnID: 'reportIssueBtn',
        },
    ];

    const filteredActions = rightActions.filter((action) => {
        if (action.type === HeaderDialogType.BACK && !canOpenProjectPage) return false;
        return true;
    });

    const isObservationDisabledByPlan = !hasObservationReports;
    const billingPageUrl = `${backendUrl}/account/subscription`;
    const planFeatureDisabledTooltip = (
        <>
            {getSentence('Plan.Feature.Disabled.Tooltip')}{' '}
            <a href={billingPageUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                Go to billing page
            </a>
        </>
    );

    const handleDialogClick = (dialog: SideDialog) => {
        if (dialog.type === HeaderDialogType.OBSERVATIONS && isObservationDisabledByPlan) {
            return;
        }

        if (dialog.onClick) {
            dialog.onClick();
            return;
        }

        if (dialog.content) {
            setActiveDialog((prev) => (prev === dialog.type ? null : dialog.type));
        }
    };

    useEffect(() => {
        if (!canUseApi && observationOpen) {
            setObservationOpen(false);
        }
    }, [canUseApi, observationOpen]);

    return (
        <Box
            className="column-icons column-icons--left"
            style={{ gridTemplateColumns: `repeat(${filteredActions.length}, minmax(0, 1fr))` }}
        >
            {filteredActions.map((dialog) => {
                const isPlanDisabled = dialog.type === HeaderDialogType.OBSERVATIONS && isObservationDisabledByPlan;
                const tooltipTitle = isPlanDisabled ? planFeatureDisabledTooltip : dialog.tooltip;
                const icon = (
                    <IconButton
                        id={dialog.btnID}
                        size="small"
                        aria-label={dialog.title}
                        disabled={isPlanDisabled}
                        onClick={dialog.onClick || dialog.content ? () => handleDialogClick(dialog) : undefined}
                        sx={{
                            p: 0.5,
                        }}
                    >
                        {dialog.Icon}
                    </IconButton>
                );

                return (
                    <Box
                        key={dialog.type}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            cursor: isPlanDisabled ? 'not-allowed' : undefined,
                        }}
                    >
                        {tooltipTitle ? (
                            <CustomTooltip side="right" title={tooltipTitle}>
                                <span>{icon}</span>
                            </CustomTooltip>
                        ) : (
                            icon
                        )}
                    </Box>
                );
            })}

            <SidePanelDialog
                open={activeDialog}
                onClose={closeDialog}
                title={rightActions.find((dialog) => dialog.type === activeDialog)?.title}
                side={'right'}
            >
                {rightActions.map((dialog) =>
                    dialog.content ? <React.Fragment key={dialog.type}>{dialog.content}</React.Fragment> : null
                )}
            </SidePanelDialog>
        </Box>
    );
};

export default RightSettings;
